import { AxiosError } from "axios";

/**
 * Extract error message from various response data formats
 */
const extractErrorMessage = (responseData: any): string | null => {
  if (!responseData) return null;
  
  // Handle string responses
  if (typeof responseData === 'string') {
    // Try to parse JSON strings
    if (responseData.trim().startsWith('{') || responseData.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(responseData);
        return extractErrorMessage(parsed);
      } catch {
        return responseData;
      }
    }
    return responseData;
  }
  
  // Handle object responses
  if (typeof responseData === 'object') {
    // Check for common error message properties
    if (responseData.message) return responseData.message;
    if (responseData.error) return responseData.error;
    if (responseData.detail) return responseData.detail;
    if (responseData.msg) return responseData.msg;
    
    // Handle validation errors (array of errors)
    if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
      const firstError = responseData.errors[0];
      if (firstError && firstError.message) {
        return firstError.message;
      }
    }
    
    // Handle nested error objects
    if (responseData.errors && typeof responseData.errors === 'object') {
      const firstError = Object.values(responseData.errors)[0];
      if (Array.isArray(firstError) && firstError.length > 0) {
        return String(firstError[0]);
      }
    }
  }
  
  return null;
};

/**
 * Clean up message formatting (capitalize, trim)
 */
const cleanMessage = (message: string): string => {
  if (!message) return "";
  
  const cleaned = message.trim();
  // Capitalize first letter if needed
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

/**
 * Extract clean error message from API response
 * Simply passes through backend error messages without overriding them
 */
const errorMessages = (error: unknown): string => {
  // Log full error for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.error("Error details:", error);
  }
  
  // Handle Zod validation errors (client-side)
  if (error && typeof error === 'object' && 'issues' in error) {
    const zodError = error as any;
    if (Array.isArray(zodError.issues) && zodError.issues.length > 0) {
      const firstIssue = zodError.issues[0];
      // Return the actual validation message from Zod
      if (firstIssue.message) {
        return cleanMessage(firstIssue.message);
      }
    }
  }
  
  // Handle Axios errors (API responses)
  if (error instanceof AxiosError) {
    const responseData = error.response?.data;
    
    // Extract message from backend response
    const errorMessage = extractErrorMessage(responseData);
    
    // If backend provided a message, use it
    if (errorMessage) {
      return cleanMessage(errorMessage);
    }
    
    // Fallback for network errors
    if (!navigator.onLine) {
      return "No internet connection. Please check your network.";
    }
    
    if (error.message.includes("Network Error")) {
      return "Network error. Please check your connection.";
    }
  }
  
  // Handle non-Axios errors
  if (error instanceof Error) {
    return cleanMessage(error.message);
  }
  
  return "Something went wrong. Please try again.";
};

export default errorMessages;